import { FieldProps } from 'lib/iterable-hook-form/FormField/types';
import isEqual from 'lodash/isEqual';
import { HOLD_UNTIL_REPLY_SWITCH_CAMPAIGN_MODAL, closeModal, openModal } from 'modules/workflows';
import { useCallback } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Select } from 'ui/selectv2/Select';
import { BaseOption, SelectProps, SelectValueType } from 'ui/selectv2/types';
import { DEFAULT_REPLIES, REPLIES } from '../HoldUntilReply.constants';

const StyledSelect = styled(Select)`
  z-index: 150;
`;

export type WhatsAppFilterSelectFieldProps<TValue extends SelectValueType = string> =
  FieldProps<TValue> &
    Omit<SelectProps, 'defaultValues' | 'isError' | 'onSelect'> & { reset?: () => void };

export const WhatsAppFilterSelectField = <TValue extends SelectValueType = string>({
  defaultValue,
  name,
  reset,
  ...rest
}: WhatsAppFilterSelectFieldProps<TValue>) => {
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const replies = useWatch({ name: REPLIES });
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name,
    defaultValue,
  });

  const handleSelect = useCallback(
    (selectedValue: string | undefined) => {
      // handle selection clearing
      if (!selectedValue) {
        onChange('');
        if (!!reset && typeof reset === 'function') reset();
        return;
      }

      if (selectedValue === value) return;

      if (isEqual(replies, DEFAULT_REPLIES)) {
        onChange(selectedValue);
      } else {
        dispatch(
          openModal(HOLD_UNTIL_REPLY_SWITCH_CAMPAIGN_MODAL, {
            handleSwitch: () => {
              onChange(selectedValue);
              setValue(REPLIES, DEFAULT_REPLIES, { shouldDirty: false });
              dispatch(closeModal(HOLD_UNTIL_REPLY_SWITCH_CAMPAIGN_MODAL));
            },
            onClose: () => {
              onChange(value);
              dispatch(closeModal(HOLD_UNTIL_REPLY_SWITCH_CAMPAIGN_MODAL));
            },
          }),
        );
      }
    },
    [replies, onChange, setValue, dispatch, value, reset],
  );

  const selectedValue = rest.options?.find((opt) => opt.value === value);

  return (
    <StyledSelect
      {...rest}
      errorMsg={error?.message}
      value={selectedValue ? [selectedValue] : undefined}
      onSelect={([selectedValue]: BaseOption[]) => {
        handleSelect(selectedValue?.value);
      }}
    />
  );
};
